import React, { useReducer } from "react";
import { useTranslation } from "react-i18next";
import { CustomersReducer, setSelectedSortOption, setFilterState } from "./state";
import { CustomersList } from "./ui/CustomersList";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import { OrderListHeaders } from "../orders/OrderList/OrderListViewHeaders";
import { ICallAPIFunction } from "../../yoello-lib/modules/API2/types";
import ListActions from "../../yoello-lib/modules/components/UI/ListActions";
import { customerColumns } from "./columns";
import { IOption, ICustomersState, IListItem } from "./types";
import { useStyles } from "./ui/styles";
import * as URLS from "../../const/URL.const";
import { useEffect } from "react";
import { useDialogRouteStages } from "../dialogRoutes/useDialogRouteStages";
import { useHistory } from "react-router";
import { dialogKeys } from "../../const/URL.const";
import { getFullName, getUniqueCustomers } from './utils/';
import { setFullNameList } from "./state/Customers.actions";
import { AlertStatusCard } from "../../yoello-lib/modules/components/UI/Alerts/AlertStatusCard";
import { Button } from "@material-ui/core";
import { ChevronRight } from "@material-ui/icons";
import { useHasCustomerExportDataPermission } from "../../hooks/useHasCustomerExportDataPermission";

interface ICustomersItemContainer {
    customersList: IListItem[];
    isLoading: boolean;
    venueId: string;
    callCustomersAPI: ICallAPIFunction<IListItem | IListItem[]>;
    path: string;
}
const initalState: ICustomersState = {
    selectedSortOption: undefined,
    filterState: '',
    nameSearchList: []
};

export function CustomersItemContainer({
    customersList,
    isLoading,
    callCustomersAPI,
    path
}: ICustomersItemContainer) {
    const history = useHistory();
    const classes = useStyles();
    const { hasCustomerExportDataPersmission } = useHasCustomerExportDataPermission();
   
    const { goToNextStep } = useDialogRouteStages(history);

    const [state, dispatch] = useReducer(
        CustomersReducer,
        initalState
    );

    useEffect(() => {
        if (state.selectedSortOption) {
            const option = state.selectedSortOption as IOption;
            callCustomersAPI(HTTPMethods.GET, {
                query: `orders?sort_field=${option.key}&sort_direction=${option.sort}`,
            })
        }
        // eslint-disable-next-line   
    }, [state.selectedSortOption])

    useEffect(() => {
        dispatch(setFullNameList(getUniqueCustomers(customersList)));
    }, [customersList]);

    const { t } = useTranslation();

    function onCustomerSelect(customer: string | null) {
        dispatch(setFilterState(customer));
    }

    function OnCustomerSelectOld(customer: IListItem | null) {
        goToNextStep(
            dialogKeys.CUSTOMER,
            {
                ComponentProps: { customerData: customer }
            }, `${path}/${customer?.id}`, null)
    }

    const searchProps = {
        placeholder: t("customers.search.placeholder"),
        onItemSelection: onCustomerSelect,
        itemsList: state.nameSearchList,
        hasDivider: true
    }

    const sortProps = {
        title: t("customers.sort.title"),
        onClick: (sortOption: IOption) => dispatch(setSelectedSortOption(sortOption)),
        sortOptions: [
            { key: "total_spend", value: t("customers.sort.TotalSpendDesc"), sort: "DESC" },
            { key: "total_spend", value: t("customers.sort.TotalSpendAsc"), sort: "ASC" },
            { key: "total_orders", value: t("customers.sort.NumberOfOrdersDesc"), sort: "DESC" },
            { key: "total_orders", value: t("customers.sort.NumberOfOrdersAsc"), sort: "ASC" },
            { key: "latest_order", value: t("customers.sort.LastTransactionDateDesc"), sort: "DESC" },
            { key: "latest_order", value: t("customers.sort.LastTransactionDateAsc"), sort: "ASC" },
        ],
        selectedSortOption: state.selectedSortOption
    }

    const filteredCustomerList = React.useMemo(() => customersList.filter((customer) => {
        if (!state.filterState || state.filterState === '') return true;

        const fullName = getFullName(customer);
        return fullName.toLowerCase() === state.filterState.toLowerCase();
    }), [customersList, state.filterState]);

    return <>
        <div className={hasCustomerExportDataPersmission ? classes.wrapper : undefined}>
        <div className={classes.controlers}>
            <ListActions hasSort={sortProps} hasSearch={searchProps} />
        </div>
        {hasCustomerExportDataPersmission && <AlertStatusCard
                alertProps={{ severity: "info" }}
                alertMessage={
                    <div className={classes.infoWrapper}>
                        {t("customers.info.export")}{" "}

                        <Button
                            onClick={() => history.push(URLS.PROFILE_REPORTS_URL)}
                            size="large"
                            variant="contained"
                            color="secondary"
                            data-component-name="CREATE_NEW_ITEM"
                            endIcon={<ChevronRight />}
                        >
                            {t("customers.info.button")}
                        </Button>
                    </div>
                }
            />}
        </div>
        <div data-component-name="CustomersListContainer">
            <div key={`columnHeaders-customersItems`} className={classes.columnsHeader}>
                {customerColumns.map((column) => <OrderListHeaders {...column} key={`col-${column.key}-${column.label}`} />)}
            </div>
            <CustomersList
                isLoading={isLoading}
                itemList={filteredCustomerList}
                columns={customerColumns}
                itemSize={80}
                onSelectItem={OnCustomerSelectOld}
                emptyListTitle={t('customers.customersContainer.emptyList')}
                className={classes.customerItem}
            />
        </div>
    </>
}
